import { DefaultLogger, StylePurple } from '@/ug-core/core/Logger'
import { EventEmitter } from '@/ug-core/core/EventEmitter'
import { ConversationNetwork } from '@/ug-core/network/ConversationNetwork'
import { ConversationNetworkEvents, INetwork } from '@/ug-core/network/types'
import { PlaybackManager, PlaybackManagerEvents } from '@/ug-core/playback-manager/PlaybackManager'
import {
  ImageChangeEvent,
  SubtitleChangeEvent,
  WordHighlightEvent,
} from '@/ug-core/playback-manager/types'
import { IVADManager, UserInputManager } from '@/ug-core/user-input-manager'
import { VADManager } from '@/ug-core/user-input-manager/VADManager'
import {
  ConversationConfig,
  ConversationError,
  ConversationState,
  InputEnvelope,
} from '@/ug-core/types'
import { UserInputManagerEvents } from '@/ug-core/user-input-manager/types'

export const ConversationManagerEvents = {
  StateChange: 'stateChange',
} as const

export class ConversationManager extends EventEmitter {
  private state: ConversationState = 'idle'
  private network: INetwork
  private vadManager: IVADManager
  private userInputManager: UserInputManager
  private playbackManager: PlaybackManager
  private config: ConversationConfig
  private interactionCompletePending = false
  private mediaStream: MediaStream | null = null
  private isTextOnly = false

  constructor(config: ConversationConfig) {
    const logger = new DefaultLogger({ category: '🧞 ConversationManager', style: StylePurple })
    super(logger)
    this.config = {
      ...config,
      capabilities: config.capabilities || {
        audio: true,
        viseme: true,
        subtitles: true,
        avatar: true,
      },
      inputCapabilities: config.inputCapabilities || {
        audio: true,
        text: true,
      },
    }
    // Dependency injection
    this.network = new ConversationNetwork(
      config.authToken!,
      config.serverUrl,
      config.componentId,
      config.personaId,
      config.contextValues
    )
    this.vadManager = new VADManager()
    this.userInputManager = new UserInputManager(
      this.config.inputCapabilities!,
      this.vadManager,
      config.recordingConfig
    )
    this.playbackManager = new PlaybackManager(this.config.capabilities!)
    this.playbackManager.wireVADToAvatar(this)
    this.playbackManager.wireConversationStateToAvatar(this)
    this.setupEventListeners()
    this.setState('uninitialized')
  }

  async initialize(): Promise<void> {
    try {
      this.logger.debug('Initializing...')
      this.setState('initializing')
      this.playbackManager.initialize()

      if (this.config.inputCapabilities?.audio && !this.mediaStream) {
        const recordingConfig = this.config.recordingConfig
        const constraints: MediaTrackConstraints = {
          echoCancellation: recordingConfig?.echoCancellation,
          noiseSuppression: recordingConfig?.noiseSuppression,
          autoGainControl: recordingConfig?.autoGainControl,
          sampleRate: recordingConfig?.sampleRate,
          channelCount: recordingConfig?.channels,
        }
        Object.keys(constraints).forEach(
          (key) =>
            constraints[key as keyof MediaTrackConstraints] === undefined &&
            delete constraints[key as keyof MediaTrackConstraints]
        )
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: constraints })
      }

      await this.userInputManager.initialize(this.mediaStream ?? undefined)
      await this.network.connect()
      this.logger.debug('Initialized successfully')
      this.setState('idle')
    } catch (error) {
      this.handleError('server_error', error as Error)
    }
  }

  async startListening(): Promise<void> {
    if (this.state !== 'idle') {
      throw new Error(`Cannot start listening from state: ${this.state}`)
    }

    try {
      await this.userInputManager.start()
      this.setState('listening')
    } catch (error) {
      this.handleError('mic_denied', error as Error)
    }
  }

  async stopListening(): Promise<void> {
    this.userInputManager.stop()
    this.setState('waiting')
  }

  async sendText(text: string): Promise<void> {
    if (!this.network.isReady()) {
      this.logger.warn('Network is not ready, cannot send text')
      return
    }
    if (this.state === 'idle') {
      await this.userInputManager.sendText(text)
      await this.setState('sending')
    }
  }

  async interrupt(): Promise<void> {
    this.playbackManager.pause()
    await this.setState('interrupted')
  }

  async pause(): Promise<void> {
    if (this.state !== 'playing') {
      this.logger.warn(`Cannot pause from state: ${this.state}`)
      return
    }
    this.playbackManager.pause()
    await this.setState('paused')
  }

  async resume(): Promise<void> {
    if (this.state !== 'paused') {
      this.logger.warn(`Cannot resume from state: ${this.state}`)
      return
    }
    // @ts-ignore - resume is not yet defined on PlaybackManager
    this.playbackManager.resume()
    await this.setState('playing')
  }

  async forceInputComplete(): Promise<void> {
    if (this.state === 'userSpeaking' || this.state === 'listening') {
      await this.userInputManager.sendInputComplete()
    } else {
      this.logger.warn(`Cannot force input complete from state: ${this.state}`)
    }
  }

  async stop(): Promise<void> {
    await this.userInputManager.stop()
    this.playbackManager.pause()
    await this.network.disconnect()
    this.setState('idle')
  }

  private async setState(newState: ConversationState): Promise<void> {
    if (this.state === newState) {
      return
    }
    const oldState = this.state
    this.state = newState
    this.config.hooks.onStateChange(newState)
    await this.emit(ConversationManagerEvents.StateChange, { oldState, newState })
    this.logger.debug(`State transition: ${oldState} -> ${newState}`)
  }

  private setupEventListeners(): void {
    this.userInputManager.on(UserInputManagerEvents.UserInput, async (envelope: InputEnvelope) => {
      await this.network.send(envelope)
      if (envelope.type === 'input_complete') {
        await this.setState('waiting')
      }
    })

    // Proxy avatar animation events via hook

    this.playbackManager.on(
      PlaybackManagerEvents.SubtitleWordChange,
      (event: WordHighlightEvent) => {
        this.config.hooks.onSubtitleHighlight?.(event)
      }
    )
    this.playbackManager.on(PlaybackManagerEvents.SubtitleChange, (event: SubtitleChangeEvent) => {
      this.config.hooks.onSubtitleChange?.(event)
    })

    this.playbackManager.on(PlaybackManagerEvents.ImageChange, (event: ImageChangeEvent) => {
      this.config.hooks.onImageChange?.(event)
    })

    this.playbackManager.on(PlaybackManagerEvents.AvatarAnimationChanged, (payload: any) => {
      this.config.hooks.onAvatarAnimationChanged?.(payload)
    })

    this.userInputManager.on(UserInputManagerEvents.UserSpeaking, async () => {
      await this.setState('userSpeaking')
    })

    this.userInputManager.on(UserInputManagerEvents.UserSilence, async () => {
      await this.setState('listening')
    })

    this.network.on(ConversationNetworkEvents.Connected, async () => {
      this.config.hooks.onNetworkStatusChange?.(true)
    })

    this.network.on(ConversationNetworkEvents.Disconnected, async () => {
      this.config.hooks.onNetworkStatusChange?.(false)
    })

    this.network.on(ConversationNetworkEvents.Message, async (message: any) => {
      // Handle interaction_complete event
      if (message.type === 'interaction_complete') {
        if (this.state === 'playing' || this.state === 'paused') {
          this.logger.debug('Interaction complete received while playing / paused, deferring.')
          this.interactionCompletePending = true
        } else {
          await this.handleInteractionComplete()
        }
      }
      if (message.type === 'interaction_error') {
        this.handleError(
          'server_error',
          new Error(message.error || 'Unknown server interaction error')
        )
      }
      if (message.type === 'metadata' && message.user_still_talking === false) {
        await this.stopListening()
      }
      if (message.type === 'string') {
        this.config.hooks.onStringMessage?.(message)
      }
      if (message.type === 'text' || message.type === 'text_complete') {
        this.config.hooks.onTextMessage?.(message)
      }
      this.playbackManager.handleMessage(message)
    })

    this.network.on(ConversationNetworkEvents.Error, async (error: Error) => {
      this.handleError('network_timeout', error)
    })

    this.playbackManager.on(PlaybackManagerEvents.PlaybackError, async (error: any) => {
      this.handleError('decode_error', error)
    })

    // Set state to 'playing' when audio playback starts
    this.playbackManager.on(PlaybackManagerEvents.Playing, async () => {
      await this.setState('playing')
    })

    this.playbackManager.on(PlaybackManagerEvents.Finished, async () => {
      // only if the last state was set by playback manager such as
      //playing - so to prever change to userTalking state and others
      if (this.state === 'playing') {
        this.userInputManager.reset()
        await this.setState('idle')
      }
      if (this.interactionCompletePending) {
        this.logger.debug('Playback finished, processing deferred interaction complete.')
        this.interactionCompletePending = false
        await this.handleInteractionComplete()
      }
    })

    // Handle about-to-complete event from playback manager
    this.playbackManager.on(PlaybackManagerEvents.AboutToComplete, () => {
      if (this.state !== 'playing') {
        this.logger.debug('AboutToComplete received but not in playing state, ignoring.')
        return
      }
      this.logger.debug('Audio about to complete, starting recording in buffering mode')
      this.userInputManager.enableAudioBuffering()
      // Start user input manager without awaiting to prevent blocking the main thread,
      // which could interfere with the final audio playback.
      this.userInputManager.start().catch((error: Error) => {
        this.logger.error('Failed to start recording on about-to-complete', error)
      })
    })
  }

  private async handleError(type: ConversationError['type'], error: Error): Promise<void> {
    const conversationError: ConversationError = {
      type,
      message: error.message,
      originalError: error,
    }

    await this.setState('error')
    this.config.hooks.onError(conversationError)
    this.logger.error(`Conversation error: ${type}`, error)
  }

  public async resumeAudioContext(): Promise<void> {
    await this.playbackManager.resumeAudioContext()
    await this.userInputManager.resumeAudioContext()
  }

  private async handleInteractionComplete(): Promise<void> {
    this.logger.debug('Interaction complete received, closing websocket and flushing audio')

    // Reset AboutToComplete logic immediately to prevent late events
    this.playbackManager.resetAboutToComplete()

    try {
      await this.startNewInteraction()
    } catch (error) {
      this.logger.error('Failed to handle interaction complete', error)
      this.handleError('network_timeout', error as Error)
    }
  }

  private async startNewInteraction() {
    this.userInputManager.reset()
    await this.network.disconnect()
    await this.network.connect()
    if (!this.network.isReady()) {
      await new Promise<void>((resolve) => {
        const onConnected = () => {
          this.network.off('connected', onConnected)
          resolve()
        }
        this.network.on('connected', onConnected)
      })
    }
    this.userInputManager.flushBufferedAudio()
    await this.userInputManager.start()
  }

  public async toggleTextOnlyInput(textOnly: boolean): Promise<void> {
    this.isTextOnly = textOnly
    await this.updateInputCapabilities()
  }

  private async updateInputCapabilities(): Promise<void> {
    this.logger.debug(`Updating input capabilities, textOnly: ${this.isTextOnly}`)
    this.config.inputCapabilities = {
      ...this.config.inputCapabilities,
      audio: !this.isTextOnly,
      text: this.isTextOnly,
    }
    // @ts-ignore
    await this.userInputManager.updateCapabilities(this.config.inputCapabilities)
    if (this.isTextOnly) {
      await this.userInputManager.stopRecording()
    } else {
      if (this.state === 'idle') {
        await this.startListening()
      }
    }
  }

  public updateContextValues(newValues: Record<string, string | number | boolean>) {
    this.network.updateContextValues(newValues);
  }
}
